import catalog from './attendance-rule-catalog.json';

/**
 * The attendance rule catalog (attendance-rule-catalog.json), typed. The same file is built into the
 * attendance workflows (blocks/workflows/attendance/build.mjs), which enforce it; the app reads it to
 * decide what to offer. A rule applies to an employee who holds its permission; in a group, only the
 * held rule with the highest priority applies. Nothing here depends on a country or region.
 */

export interface BreakTier {
  aboveWorkSeconds: number;
  /** Inclusive; absent for the last tier. */
  upToWorkSeconds?: number;
  minBreakSeconds: number;
  /** Translation key shown when the tier's minimum is not met. */
  messageKey: string;
}

export interface LunchParams {
  /** `dayStart`: [fromMinutes, toMinutes] of the local day; `clockIn`: minutes after the first clock-in. */
  windowFrom: 'dayStart' | 'clockIn';
  fromMinutes: number;
  toMinutes: number;
  minCountedSeconds: number;
  /** The day is charged at least `minCountedSeconds` of break even without a lunch. */
  chargeMinimumWithoutLunch: boolean;
  /** Recorded at clock-out when no break was taken. */
  autoLunch: { fromMinutes: number; toMinutes: number } | null;
}

export interface AttendanceRuleParams {
  clockInCutoff: { cutoffMinutes: number; exemptFlexible: boolean };
  clockOutWait: { waitSeconds: number };
  /** Shares of the corrected day's scheduled hours (the HR term's WorkingHours for that weekday). */
  correctionMinHours: { fullDayPercent: number; halfDayPercent: number };
  minBreakLength: { minSeconds: number };
  breakTiers: { tiers: BreakTier[] };
  maxWork: { maxWorkSeconds: number };
  officeNetwork: { approvedIps: string[] };
  lunch: LunchParams;
  correctionFine: { amount: number };
  notClockedOutReminder: { atMinutes: number };
  workHoursReminder: {
    firstAfterWorkSeconds: number;
    secondAfterWorkSeconds: number;
    shortDayLeadSeconds: number;
  };
  forceClockOutAtMaxWork: { maxWorkSeconds: number; atExpectedWithoutOvertimeBelowSeconds: number };
  forceClockOutAtDayEnd: { atMinutes: number };
  breakReminders: {
    lunchFirstSeconds: number;
    lunchEscalateSeconds: number;
    regularEscalateSeconds: number;
  };
  breakNotices: Record<string, never>;
  clockOutCaptcha: { provider: 'recaptcha' };
  nonWorkdayOvertime: { workedCreditPercent: number; bonusPercent: number };
}

export type AttendanceRuleGroup = keyof AttendanceRuleParams;

export interface AttendanceRule<G extends AttendanceRuleGroup = AttendanceRuleGroup> {
  code: string;
  group: G;
  permission: string;
  priority: number;
  description: string;
  params: AttendanceRuleParams[G];
}

export type ActiveAttendanceRules = { [G in AttendanceRuleGroup]?: AttendanceRule<G> };

export const ATTENDANCE_RULES = catalog.rules as AttendanceRule[];

export const ATTENDANCE_CONSTANTS = catalog.constants as {
  openShiftMaxSeconds: number;
  defaultExpectedWorkSeconds: number;
  maxCorrectionSpanSeconds: number;
};

/** The rule of each group that binds the holder of `held`: in a group, the highest priority held. */
export const activeAttendanceRules = (
  held: (permission: string) => boolean
): ActiveAttendanceRules => {
  const byGroup: Partial<Record<AttendanceRuleGroup, AttendanceRule>> = {};
  for (const rule of ATTENDANCE_RULES) {
    if (!held(rule.permission)) continue;
    const current = byGroup[rule.group];
    if (!current || rule.priority > current.priority) byGroup[rule.group] = rule;
  }
  return byGroup as ActiveAttendanceRules;
};
